"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { idToUrlSegment } from "@/utilities/mappers/idUrlFormat";
import type { HeroListItem } from "./headerModel";

// useLayoutEffect logs a warning on the server; fall back to useEffect there.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
interface HeroMenuProps {
  competitions: HeroListItem[];
  teams: HeroListItem[];
  basePath: string;
  textMode?: "light" | "dark";
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/**
 * Hero Menu (Mega Menu) component.
 * Displays a mega menu panel with competitions and teams on hover.
 */
export function HeroMenu(t0) {
  const $ = _c(24);
  const {
    competitions,
    teams,
    basePath,
    textMode: t1,
    isOpen,
    onMouseEnter,
    onMouseLeave
  } = t0;
  const textMode = t1 === undefined ? "light" : t1;
  const menuRef = useRef(null);
  const labels = useLabels();
  const [topPx, setTopPx] = useState(null);
  let t2;
  let t3;
  if ($[0] !== isOpen) {
    t2 = () => {
      if (!isOpen) {
        return;
      }
      const panel = menuRef.current;
      if (!panel) {
        return;
      }
      const headerEl = panel.closest("header");
      if (!headerEl) {
        return;
      }
      const update = () => {
        setTopPx(headerEl.getBoundingClientRect().bottom);
      };
      update();
      window.addEventListener("scroll", update, {
        passive: true
      });
      window.addEventListener("resize", update);
      return () => {
        window.removeEventListener("scroll", update);
        window.removeEventListener("resize", update);
      };
    };
    t3 = [isOpen];
    $[0] = isOpen;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useIsomorphicLayoutEffect(t2, t3);
  let t4;
  if ($[3] !== basePath) {
    t4 = competitionId => {
      const seg = idToUrlSegment(competitionId);
      return {
        news: `${basePath}/football/competitions/${seg}`,
        matches: `${basePath}/football/competitions/${seg}/matches`,
        standings: `${basePath}/football/competitions/${seg}/standings`
      };
    };
    $[3] = basePath;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const buildCompetitionUrls = t4;
  const textColorClass = textMode === "light" ? "text-white" : "text-foreground";
  const hoverBgClass = textMode === "light" ? "hover:bg-white/10" : "hover:bg-muted";
  const mutedTextClass = textMode === "light" ? "text-white/70" : "text-muted-foreground";
  const mutedHoverClass = textMode === "light" ? "hover:text-white" : "hover:text-foreground";
  const t5 = topPx != null ? `${topPx}px` : "var(--header-height, 64px)";
  let t6;
  if ($[5] !== t5) {
    t6 = {
      top: t5,
      background: "linear-gradient(135deg, var(--header-gradient-from, var(--primary)) 0%, var(--header-gradient-to, var(--primary)) 100%)"
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const panelStyle = t6;
  if (competitions.length === 0) {
    return null;
  }
  let t7;
  if ($[7] !== basePath || $[8] !== buildCompetitionUrls || $[9] !== competitions || $[10] !== hoverBgClass || $[11] !== isOpen || $[12] !== labels || $[13] !== mutedHoverClass || $[14] !== mutedTextClass || $[15] !== onMouseEnter || $[16] !== onMouseLeave || $[17] !== panelStyle || $[18] !== teams || $[19] !== textColorClass || $[20] !== textMode) {
    t7 = isOpen && <div ref={menuRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} className={`fixed left-0 right-0 backdrop-blur-sm shadow-xl z-40 ${textColorClass}`} style={panelStyle}><div className="max-w-350 mx-auto p-4"><div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 mb-6">{competitions.map(item => {
            const urls = buildCompetitionUrls(item.entityId);
            const resolvedLogo = item.logo;
            return <div key={item.entityId} className={`flex items-start gap-4 py-2 px-3 rounded-lg transition-colors ${hoverBgClass}`}><Link href={urls.news} prefetch={false} className="shrink-0">{resolvedLogo && <Image src={resolvedLogo} alt={item.label || ""} width={64} height={64} className="w-16 h-16 rounded-md object-contain" />}</Link><div className="flex flex-col min-w-0"><Link href={urls.news} prefetch={false} className="flex items-center gap-1 font-bold hover:underline mb-1"><span className="truncate">{item.label || item.entityId}</span><ChevronRight className="w-4 h-4 shrink-0" /></Link><div className="flex items-center gap-1 text-sm"><Link href={urls.news} prefetch={false} className={`${mutedTextClass} ${mutedHoverClass} hover:underline`}>{labels.matchPage.tabs.news}</Link><span className={mutedTextClass}>·</span><Link href={urls.matches} prefetch={false} className={`${mutedTextClass} ${mutedHoverClass} hover:underline`}>{labels.competitions.matches}</Link><span className={mutedTextClass}>·</span><Link href={urls.standings} prefetch={false} className={`${mutedTextClass} ${mutedHoverClass} hover:underline`}>{labels.competitions.standings}</Link></div></div></div>;
          })}</div>{teams.length > 0 && <div className={`border-t pt-6 ${textMode === "light" ? "border-white/10" : "border-border"}`}><div className="flex flex-wrap gap-1 lg:gap-2">{teams.map(team => <Link key={team.entityId} href={`${basePath}/football/teams/${idToUrlSegment(team.entityId)}`} prefetch={false} className={`shrink-0 transition-all p-1 rounded-lg ${textMode === "light" ? "hover:bg-white/10" : "hover:bg-muted"}`} title={team.label || team.entityId}>{team.logo && <Image src={team.logo} alt={team.label || ""} width={48} height={48} className="w-8 h-8 lg:w-10 lg:h-10 object-contain" />}</Link>)}</div></div>}</div></div>;
    $[7] = basePath;
    $[8] = buildCompetitionUrls;
    $[9] = competitions;
    $[10] = hoverBgClass;
    $[11] = isOpen;
    $[12] = labels;
    $[13] = mutedHoverClass;
    $[14] = mutedTextClass;
    $[15] = onMouseEnter;
    $[16] = onMouseLeave;
    $[17] = panelStyle;
    $[18] = teams;
    $[19] = textColorClass;
    $[20] = textMode;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t7) {
    t8 = <>{t7}</>;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}